/**
 * The 3Halves half of a membership transaction: what the fan's browser has to do at the processor,
 * because only the browser holds the fan's access token.
 *
 * Three tasks, named by the server (`membership.task`):
 *
 *   join      status → enrol if not enrolled → cancel any open withdrawal → check again
 *   withdraw  read → raise if nothing is open → confirm
 *   cancel    read → cancel an open withdrawal → check again   (a rejoined fan's repair)
 *
 * **Every task starts by READING the processor's state** and acts on what is there, not on what an
 * earlier transaction meant to leave behind. That is what makes a resumed, repeated or repaired
 * leg safe: each step is idempotent, and a step that is already done is skipped.
 *
 * **A withdraw that adopts a request it did not raise checks that request's `eraseData`** against
 * the leave policy, and reports `confirmed:policy_mismatch` when they differ -- a code the journal
 * row keeps, not only a console line. The request may not be ours: the middleware opens one itself,
 * with `eraseData: false`, when a fan's next token shows the opt-in withdrawn (their #580), and a
 * leg resumed on a page that already has that token finds it standing. The fan still leaves either
 * way -- a second request cannot be opened, the middleware hands back the one it has -- so the code
 * is what tells a person that the fan was promised an erasure the processor was never asked for.
 *
 * **A fence is checked before EVERY call that writes** -- and before the first read -- and it
 * throws when the leg is no longer the fan's current intent: a different fan is signed in, the
 * marker names a newer transaction (the fan pressed the other button in another tab), or, for a
 * join, consent is no longer granted. So a late `joinLoyalty` after the fan pressed Leave elsewhere
 * is never sent, which is the "no processing call while consent is false" rule; and an account
 * switch cannot enrol a fan who never consented.
 *
 * **In a join, the cancel comes AFTER the enrolment**, where D-091 put it before. A join that is
 * aborted after its cancel would leave an enrolled fan with no withdrawal standing; after the
 * enrolment, an abort leaves the withdrawal where it was. The order costs nothing after a PLAIN
 * leave, where the fan stays enrolled until an admin runs the executor and the status read skips
 * the join call. **It is not free on an install that asks for erasure**, which this one does:
 * measured 2026-09-30, a confirmed erasure disassociates the fan at once, so the next read builds a
 * replacement account that is NOT enrolled and the join call IS made -- it enrolls the replacement
 * -- and only the cancel after it restores the original account and discards the replacement. A
 * cancel that does not land leaves the fan on the replacement. Whether to cancel first when the open
 * request is a confirmed erasure is an open question, not a decision: see
 * docs/subsystems/loyalty-withdrawal.md, *The browser half*.
 *
 * Nothing here talks to WordPress. The caller reports the result (./membership.js), and the server
 * decides what it means. Everything is injected so processorLeg.test.js drives every branch.
 */

import { joinFailureCode } from '@drewling/urc-loyalty-api';

/** What a leg came to, as the caller reports it. `fenced` is the caller's to translate. */
export const LEG = {
	DONE: 'done',
	REFUSED: 'refused',
	RETRY: 'retry',
	FENCED: 'fenced',
};

/**
 * Join refusals that no retry can clear, so the join is undone rather than retried.
 * `account_unavailable` is a suppressed fan (D-091) -- a defensive entry, since 3Halves report that
 * refusal removed on 2026-09-27 (#660, not measured here; see ../withdrawal.js SUPPRESSED).
 */
const DEFINITIVE_JOIN_FAILURES = [
	'account_unavailable',
	'consent_required',
	'invalid_favourite_team',
];

/** Withdrawal states in which a request can still be cancelled. */
const CANCELLABLE = [ 'requested', 'confirmed' ];

/** Withdrawal states that already mean "the processor has the fan's request". */
const WITHDRAWN = [ 'confirmed', 'executing', 'completed' ];

/** Thrown by a fence. */
export class FenceError extends Error {
	/**
	 * @param {string} reason 'identity', 'superseded', 'consent' or 'aborted'.
	 */
	constructor( reason ) {
		super( `The membership leg stopped: ${ reason }.` );
		this.name = 'FenceError';
		this.reason = reason;
	}
}

/**
 * @param {Object}   options               Wiring.
 * @param {Object}   options.client        The (consent-gated) loyalty client: getEnrolmentStatus,
 *                                         joinLoyalty.
 * @param {Object}   options.withdrawal    A ../withdrawal.js client.
 * @param {Function} options.requestIdOf   ../withdrawal.js requestIdOf.
 * @param {Function} options.isSuppressed  ../withdrawal.js isSuppressed.
 * @param {Function} [options.failureCode] Join refusal reader; the library's by default.
 * @param {Function} [options.warn]        Logger.
 * @return {{run: Function}} The leg runner.
 */
export function createProcessorLeg( {
	client,
	withdrawal,
	requestIdOf,
	isSuppressed,
	failureCode = joinFailureCode,
	warn = () => {},
} ) {
	/**
	 * @param {string}   task                           'join', 'withdraw' or 'cancel'.
	 * @param {Object}   context                        Per-run inputs.
	 * @param {Function} context.fence                  Throws FenceError when the leg must stop.
	 * @param {Object}   [context.signal]               Abort signal for the library's calls.
	 * @param {boolean}  [context.eraseData]            Leave policy.
	 * @param {boolean}  [context.mustVerifyWithdrawal] A join that must SEE no open withdrawal
	 *                                                  before it counts as done (it superseded a leave, or the
	 *                                                  pre-check found a request).
	 * @return {Promise<{result: string, code: string, wrote: boolean, joined: *, fence: *}>} Never
	 *                                     rejects.
	 */
	function run( task, context ) {
		const state = { wrote: false, joined: null };
		const tasks = {
			join: () => join( context, state ),
			withdraw: () => withdraw( context, state ),
			cancel: () => cancelOpen( context, state, 'cancelled' ),
		};
		const body = tasks[ task ] || tasks.cancel;

		return Promise.resolve()
			.then( body )
			.then(
				outcome => ( {
					...outcome,
					wrote: state.wrote,
					joined: state.joined,
					fence: null,
				} ),
				error => {
					if ( error instanceof FenceError ) {
						return {
							result: LEG.FENCED,
							code: `fenced:${ error.reason }`,
							wrote: state.wrote,
							joined: state.joined,
							fence: error.reason,
						};
					}

					warn( 'the membership leg failed unexpectedly', error );

					return {
						result: LEG.RETRY,
						code: `error:${ kindOf( error ) }`,
						wrote: state.wrote,
						joined: state.joined,
						fence: null,
					};
				}
			);
	}

	/**
	 * @param {Object} context Per-run inputs.
	 * @param {Object} state   What was written, for the report.
	 * @return {Promise<Object>} The outcome.
	 */
	async function join( context, state ) {
		const { fence, signal } = context;

		fence();

		if ( ( await readEnrolled( signal ) ) !== true ) {
			fence();
			state.wrote = true;

			try {
				state.joined = await client.joinLoyalty( { consentToLoyalty: true }, { signal } );
			} catch ( error ) {
				const code = failureCode( error );

				if ( code && DEFINITIVE_JOIN_FAILURES.includes( code ) ) {
					return { result: LEG.REFUSED, code };
				}

				// Anything else may have landed: a timeout after the request left, a 5xx on the
				// way back. Ask before deciding, so a join that DID land commits instead of being
				// undone over a lost answer.
				if ( ( await readEnrolled( signal ) ) !== true ) {
					return { result: LEG.RETRY, code: `ambiguous:${ code || kindOf( error ) }` };
				}
			}
		}

		const cleared = await cancelOpen(
			context,
			state,
			'enrolled',
			Boolean( context.mustVerifyWithdrawal )
		);

		// The fan IS enrolled by now. If clearing the withdrawal did not finish, say so in the
		// code: a join undone from here leaves the processor holding a member, and the server
		// flags exactly that (`aborted_unverified`) off this prefix.
		return cleared.result === LEG.RETRY
			? { ...cleared, code: `enrolled:${ cleared.code }` }
			: cleared;
	}

	/**
	 * Cancel whatever withdrawal is open, and check it is gone.
	 *
	 * @param {Object}  context   Per-run inputs.
	 * @param {Object}  state     What was written.
	 * @param {string}  doneCode  The code a clean finish reports.
	 * @param {boolean} [mustSee] Whether an unreadable withdrawal blocks a clean finish. A repair
	 *                            always must; a plain join only when there is reason to expect a
	 *                            request (D-091's pre-check failing open is the same trade).
	 * @return {Promise<Object>} The outcome.
	 */
	async function cancelOpen( context, state, doneCode, mustSee = true ) {
		const { fence } = context;

		fence();

		const looked = await withdrawal.current();

		if ( isSuppressed( looked ) ) {
			return { result: LEG.REFUSED, code: 'account_unavailable' };
		}

		if ( looked.outcome === 'not-provisioned' ) {
			return { result: LEG.DONE, code: doneCode };
		}

		if ( looked.outcome !== 'ok' ) {
			return mustSee
				? { result: LEG.RETRY, code: `withdrawal_unreadable:${ looked.outcome }` }
				: { result: LEG.DONE, code: doneCode };
		}

		const request = looked.body;

		if ( ! request || ! CANCELLABLE.includes( request.state ) ) {
			if ( request && request.state === 'executing' ) {
				// The executor has the request: erasure follows, and nothing on this side can stop
				// it. (Suppression followed too until 3Halves report removing it on 2026-09-27.)
				return { result: LEG.REFUSED, code: 'withdrawal_executing' };
			}

			return { result: LEG.DONE, code: doneCode };
		}

		fence();
		state.wrote = true;

		const cancelled = await withdrawal.cancel( requestIdOf( request ) );

		if ( cancelled.outcome !== 'ok' ) {
			return { result: LEG.RETRY, code: `cancel:${ cancelled.outcome }` };
		}

		// The join commits on VERIFIED state: a raise from a closed page can land after the cancel.
		const again = await withdrawal.current();

		if ( again.outcome === 'ok' && again.body && CANCELLABLE.includes( again.body.state ) ) {
			return { result: LEG.RETRY, code: 'withdrawal_still_open' };
		}

		return { result: LEG.DONE, code: doneCode };
	}

	/**
	 * @param {Object} context Per-run inputs.
	 * @param {Object} state   What was written.
	 * @return {Promise<Object>} The outcome.
	 */
	async function withdraw( context, state ) {
		const { fence, eraseData } = context;

		fence();

		const looked = await withdrawal.current();

		if ( isSuppressed( looked ) ) {
			// Erasure already completed: the processor has nothing left to withdraw. Defensive --
			// 3Halves report this refusal removed (see ../withdrawal.js SUPPRESSED).
			return { result: LEG.DONE, code: 'suppressed' };
		}

		if ( looked.outcome === 'not-provisioned' ) {
			return { result: LEG.DONE, code: 'not_provisioned' };
		}

		if ( looked.outcome !== 'ok' ) {
			return { result: LEG.RETRY, code: `unreadable:${ looked.outcome }` };
		}

		const current = looked.body;

		// Both branches below ADOPT a request this leg did not raise, so both check it.
		if ( current && WITHDRAWN.includes( current.state ) ) {
			return {
				result: LEG.DONE,
				code: confirmedCode( noteSubstitution( current, eraseData, 'adopted' ) ),
			};
		}

		let requestId = current && current.state === 'requested' ? requestIdOf( current ) : '';
		let mismatch = requestId ? noteSubstitution( current, eraseData, 'adopted' ) : false;

		if ( ! requestId ) {
			fence();
			state.wrote = true;

			const raised = await withdrawal.raise( { withdrawProgramme: true, eraseData } );

			if ( raised.outcome === 'not-provisioned' ) {
				return { result: LEG.DONE, code: 'not_provisioned' };
			}

			if ( raised.outcome !== 'ok' ) {
				return { result: LEG.RETRY, code: `raise:${ raised.outcome }` };
			}

			mismatch = noteSubstitution( raised.body, eraseData, 'returned' );
			requestId = requestIdOf( raised.body );

			if ( ! requestId ) {
				return { result: LEG.RETRY, code: 'raise:no_id' };
			}
		}

		fence();
		state.wrote = true;

		let confirmed = await withdrawal.confirm( requestId );

		if ( confirmed.outcome === 'conflict' ) {
			// The request we hold is not the one the processor now has. Pick up the current one --
			// a second raise returns the existing request -- and confirm THAT id, once. Fenced
			// like every other write: an account switch mid-leg must not raise on the next fan.
			fence();

			const again = await withdrawal.raise( { withdrawProgramme: true, eraseData } );

			if ( again.outcome === 'ok' ) {
				// Whatever this confirms is now the fan's request, so it is the one that is checked.
				mismatch = noteSubstitution( again.body, eraseData, 'returned' );
				fence();
			}

			confirmed =
				again.outcome === 'ok'
					? await withdrawal.confirm( requestIdOf( again.body ) )
					: again;
		}

		if ( confirmed.outcome !== 'ok' ) {
			return { result: LEG.RETRY, code: `confirm:${ confirmed.outcome }` };
		}

		return { result: LEG.DONE, code: confirmedCode( mismatch ) };
	}

	/**
	 * @param {Object} [signal] Abort signal.
	 * @return {Promise<boolean|null>} Enrolled, not enrolled, or unreadable.
	 */
	function readEnrolled( signal ) {
		return Promise.resolve()
			.then( () => client.getEnrolmentStatus( { signal } ) )
			.then(
				status => {
					if ( status && status.state === 'enrolled' ) {
						return true;
					}

					return status && status.state === 'not_enrolled' ? false : null;
				},
				() => null
			);
	}

	/**
	 * Say so when the request this leg is about to act on was not raised with the leave policy's
	 * `eraseData` -- a request the processor handed back from a raise, or one the leg found already
	 * open and adopted. See the same warning's history in ../leaveLoyalty.js.
	 *
	 * A request with no `eraseData` field at all is not a mismatch: nothing can be said about it.
	 *
	 * @param {Object|null}          body      The request's unwrapped payload.
	 * @param {boolean}              eraseData The leave policy.
	 * @param {'returned'|'adopted'} how       How the leg came by it, for the log.
	 * @return {boolean} Whether the two differ.
	 */
	function noteSubstitution( body, eraseData, how ) {
		if ( ! body || body.eraseData === undefined || body.eraseData === eraseData ) {
			return false;
		}

		warn(
			how === 'adopted'
				? 'the leave leg adopted an open request with a different eraseData'
				: 'the processor returned an existing request with a different eraseData',
			{
				asked: eraseData,
				returned: body.eraseData,
				source: body.source,
				state: body.state,
			}
		);

		return true;
	}

	return { run };
}

/**
 * The code a finished withdraw reports.
 *
 * @param {boolean} mismatch Whether the request it confirmed differs from the leave policy.
 * @return {string} `confirmed`, or `confirmed:policy_mismatch`.
 */
function confirmedCode( mismatch ) {
	return mismatch ? 'confirmed:policy_mismatch' : 'confirmed';
}

/**
 * A short, non-identifying name for an error, for a report code.
 *
 * @param {*} error Anything.
 * @return {string} The name, lower-cased, or 'unknown'.
 */
function kindOf( error ) {
	if ( error && error.timedOut ) {
		return 'timeout';
	}

	const name = error && typeof error.name === 'string' ? error.name : '';

	return name ? name.toLowerCase() : 'unknown';
}
